// src/pages/ClientDetail.jsx
import React, { useEffect, useState, useMemo, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { toast } from 'react-toastify';
import { handleDeleteItem } from '../utils/crmActions';

import EditClientModal from '../components/EditClientModal';
// ❌ No legacy AddInvoiceModal (we route to /invoices/new)
import AddPaymentModal from '../components/AddPaymentModal';
import EditInvoiceModal from '../components/EditInvoiceModal';
import EditPaymentModal from '../components/EditPaymentModal';

// 🔄 Unified Work tab (task | job | appointment)
import ClientWorkTab from './client-tabs/ClientWorkTab';

// 🔄 Unified Add Work modal
import AddWorkModal from '../components/work/AddWorkModal';

import ClientInfoTab from './client-tabs/ClientInfoTab';
import ClientInvoicesTab from './client-tabs/ClientInvoicesTab';
import ClientPaymentsTab from './client-tabs/ClientPaymentsTab';
import ClientQuotesTab from './client-tabs/ClientQuotesTab';

import ClientSummaryStrip from '../components/ClientSummaryStrip';

import useAIStore from '../store/useAIStore';
import {
  PencilLine,
  Plus,
  Mail,
  Phone,
  Building2,
  Globe,
  MapPin,
  Tag as TagIcon,
  ChevronDown,
  User as UserIcon,
} from 'lucide-react';

/* -------------------------------------------------------------------------- */
/* helpers                                                                    */
/* -------------------------------------------------------------------------- */

function formatAddress(address) {
  if (!address) return '';
  if (typeof address === 'string') return address;
  const { street, city, state, zip, country, raw } = address || {};
  const structured = [street, city, state, zip, country].filter(Boolean).join(', ');
  return structured || raw || '';
}
function ensureHttp(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}
function formatUser(u) {
  if (!u) return '';
  if (typeof u === 'string') return u;
  return u.name || u.email || '';
}
function formatDate(dt) {
  if (!dt) return '';
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return String(dt);
  }
}

/** ✅ Robust extractor for {items,total} regardless of shape */
function extractItemsAndTotal(payload) {
  if (Array.isArray(payload)) return { items: payload, total: payload.length };

  // { items: [...], total } OR { items: [...], meta: { total } }
  if (payload?.items) {
    return {
      items: payload.items,
      total: payload?.meta?.total ?? payload?.total ?? payload.items.length,
    };
  }

  // { data: [...] , meta: {...} }
  if (Array.isArray(payload?.data)) {
    return {
      items: payload.data,
      total: payload?.meta?.total ?? payload?.total ?? payload.data.length,
    };
  }

  // { data: { items: [...], meta: {...} } }
  if (payload?.data?.items) {
    return {
      items: payload.data.items,
      total: payload?.data?.meta?.total ?? payload?.data?.total ?? payload.data.items.length,
    };
  }

  return { items: [], total: 0 };
}

/** primary display date for a work item */
function workPrimaryDate(item) {
  return item?.schedule?.start || item?.schedule?.end || item?.dueDate || null;
}

/** treat these as "closed" to exclude from "open" KPIs */
const CLOSED_STATUSES = new Set(['done', 'completed', 'cancelled', 'invoiced', 'no_show', 'void']);
const isClosed = (status) => CLOSED_STATUSES.has(String(status || '').toLowerCase());

function statusChipClass(value) {
  switch (value) {
    case 'active':
      return 'bg-green-100 text-green-700';
    case 'lead':
    case 'prospect':
      return 'bg-amber-100 text-amber-700';
    case 'inactive':
    case 'archived':
      return 'bg-gray-200 text-gray-700';
    case 'lost':
      return 'bg-rose-100 text-rose-700';
    default:
      return 'bg-blue-100 text-blue-700';
  }
}

const Card = ({ className = '', children }) => (
  <div className={`rounded-xl bg-white border border-gray-100 shadow-sm ${className}`}>{children}</div>
);

const InfoItem = ({ icon: Icon, label, value, href }) => {
  const content = value || '—';
  return (
    <div className="flex items-start gap-2 text-sm">
      <div className="mt-0.5 text-gray-500"><Icon size={16} /></div>
      <div className="min-w-0">
        <div className="text-gray-500">{label}</div>
        {href ? (
          <a href={href} target="_blank" rel="noreferrer" className="truncate font-medium text-gray-900 hover:underline">
            {content}
          </a>
        ) : (
          <div className="truncate font-medium text-gray-900">{content}</div>
        )}
      </div>
    </div>
  );
};

const PillTabButton = ({ active, onClick, children, count }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
      active ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`}
  >
    <span>{children}</span>
    {typeof count === 'number' ? (
      <span
        className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-2 text-xs ${
          active ? 'bg-white/20 text-white' : 'bg-white text-gray-700'
        }`}
      >
        {count}
      </span>
    ) : null}
  </button>
);

const AvatarCircle = ({ name, url }) => {
  const initials =
    (name || '')
      .split(' ')
      .filter(Boolean)
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'C';

  return (
    <div className="h-14 w-14 shrink-0 rounded-full border border-blue-200 bg-gradient-to-br from-blue-100 to-blue-300 text-blue-700 shadow flex items-center justify-center text-lg font-extrabold overflow-hidden">
      {url ? <img src={url} alt="Avatar" className="h-full w-full object-cover" /> : initials}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* top-level UI parts                                                         */
/* -------------------------------------------------------------------------- */

const Hero = memo(function Hero({ client, canEdit, onEdit }) {
  return (
    <Card className="p-6">
      <div className="flex flex-col sm:flex-row gap-6">
        <AvatarCircle name={client.name} url={client.avatarUrl} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-gray-900 truncate">{client.name}</h1>
            {client.status ? (
              <span className={`rounded px-2 py-0.5 text-xs ${statusChipClass(client.status)}`}>{client.status}</span>
            ) : null}
            <span className="text-xs text-gray-400">Client Profile</span>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <InfoItem icon={Building2} label="Company" value={client.company} />
            <InfoItem icon={Mail} label="Email" value={client.email} href={client.email ? `mailto:${client.email}` : undefined} />
            <InfoItem icon={Phone} label="Phone" value={client.phone} href={client.phone ? `tel:${client.phone}` : undefined} />
            <InfoItem icon={Globe} label="Website" value={client.website} href={client.website ? ensureHttp(client.website) : undefined} />
            <InfoItem icon={MapPin} label="Address" value={formatAddress(client.address)} />
            <InfoItem icon={UserIcon} label="Contact Person" value={client.contactPerson} />
          </div>

          {Array.isArray(client.tags) && client.tags.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 text-xs text-gray-500"><TagIcon size={14} /> Tags</span>
              {client.tags.map((tag) => (
                <span key={tag} className="rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                  {tag}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {canEdit && (
          <div className="sm:self-start">
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <PencilLine size={16} />
              Edit
            </button>
          </div>
        )}
      </div>
    </Card>
  );
});

/** 🔧 FIX: render the dropdown *inside* the button's relative wrapper so it anchors correctly. */
const TabsHeader = memo(function TabsHeader({
  activeTab,
  setActiveTab,
  counts,
  canEdit,
  setOpenNewMenu,
  openNewMenu,
  onNewQuote,
  onNewInvoice,
  onNewWork,
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2 md:gap-3">
        <PillTabButton active={activeTab === 'info'} onClick={() => setActiveTab('info')}>Client Info</PillTabButton>
        <PillTabButton active={activeTab === 'quotes'} onClick={() => setActiveTab('quotes')} count={counts.quotes}>Quotes</PillTabButton>
        <PillTabButton active={activeTab === 'invoices'} onClick={() => setActiveTab('invoices')} count={counts.invoices}>Invoices</PillTabButton>
        <PillTabButton active={activeTab === 'work'} onClick={() => setActiveTab('work')} count={counts.work}>Work</PillTabButton>
        <PillTabButton active={activeTab === 'payments'} onClick={() => setActiveTab('payments')} count={counts.payments}>Payments</PillTabButton>
      </div>

      {canEdit && (
        <div className="relative">
          <button
            onClick={() => setOpenNewMenu((v) => !v)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
          >
            <Plus size={16} />
            New
            <ChevronDown size={16} />
          </button>

          {/* ▼ Dropdown anchored to the same relative wrapper */}
          {openNewMenu && (
            <div
              className="absolute right-0 mt-2 w-48 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden z-50"
              onMouseLeave={() => setOpenNewMenu(false)}
            >
              <button
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                onClick={onNewQuote}
              >
                Quote
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                onClick={onNewInvoice}
              >
                Invoice
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                onClick={onNewWork}
              >
                Work (Task/Job/Appointment)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const RightRail = memo(function RightRail({ client }) {
  const addressStr = formatAddress(client.address);
  const website = client.website ? ensureHttp(client.website) : '';

  return (
    <Card className="p-5">
      <div className="space-y-6">
        {(addressStr || website || client.email || client.phone) && (
          <section>
            <div className="text-sm font-semibold text-gray-800">Quick Links</div>
            <div className="mt-2 flex flex-col gap-2 text-sm">
              {addressStr ? (
                <a
                  className="font-semibold text-blue-700 hover:underline"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressStr)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Address in Google Maps
                </a>
              ) : null}
              {website ? (
                <a
                  className="font-semibold text-blue-700 hover:underline"
                  href={website}
                  target="_blank"
                  rel="noreferrer"
                >
                  Visit Website
                </a>
              ) : null}
              {client.email ? (
                <a className="font-semibold text-blue-700 hover:underline" href={`mailto:${client.email}`}>
                  Send Email
                </a>
              ) : null}
              {client.phone ? (
                <a className="font-semibold text-blue-700 hover:underline" href={`tel:${client.phone}`}>
                  Call Phone
                </a>
              ) : null}
            </div>
          </section>
        )}

        {(client.createdAt || client.updatedAt || client.createdBy || client.updatedBy || (Array.isArray(client.activityLog) && client.activityLog.length > 0)) && (
          <section>
            <div className="text-sm font-semibold text-gray-800">Meta & Activity</div>

            {(client.createdAt || client.updatedAt || client.createdBy || client.updatedBy) ? (
              <div className="mt-2 text-xs text-gray-500">
                <div className="flex flex-col gap-1">
                  <div>
                    Created: {formatDate(client.createdAt) || '—'} {client.createdBy ? `by ${formatUser(client.createdBy)}` : ''}
                  </div>
                  <div>
                    Updated: {formatDate(client.updatedAt) || '—'} {client.updatedBy ? `by ${formatUser(client.updatedBy)}` : ''}
                  </div>
                </div>
              </div>
            ) : null}

            {Array.isArray(client.activityLog) && client.activityLog.length > 0 ? (
              <div className="mt-4 space-y-3">
                <div className="text-xs font-semibold text-gray-500">Recent Activity</div>
                <div className="space-y-2">
                  {client.activityLog.map((act, i) => {
                    const who = formatUser(act.by) || '—';
                    const when = formatDate(act.date) || '—';
                    return (
                      <div key={i} className="border-l-2 border-blue-200 pl-3 text-sm">
                        <div className="text-[11px] text-gray-500 mb-0.5">
                          {who} • {when} • {act.type || 'activity'}
                        </div>
                        <div className="text-gray-900">{act.details || '—'}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </section>
        )}
      </div>
    </Card>
  );
});

/* -------------------------------------------------------------------------- */
/* main component                                                             */
/* -------------------------------------------------------------------------- */

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('info');

  const [invoices, setInvoices] = useState([]);
  const [invoiceCount, setInvoiceCount] = useState(0); // ✅ badge uses true total
  const [payments, setPayments] = useState([]);
  const [quotes, setQuotes] = useState([]);

  // Unified Work state (replaces old "tasks")
  const [workCount, setWorkCount] = useState(0);   // badge
  const [workItems, setWorkItems] = useState([]);  // for KPIs only (small page)

  const [quoteCount, setQuoteCount] = useState(0); // badge via meta/total

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showWorkModal, setShowWorkModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [openNewMenu, setOpenNewMenu] = useState(false);

  // reserved for future use
  // eslint-disable-next-line no-unused-vars
  const triggerAI = useAIStore((state) => state.triggerAI);

  useEffect(() => {
    fetchClient();
    fetchInvoices();
    fetchPayments();
    fetchQuotes();
    fetchWorkSummary(); // ✅ replaces fetchClientTasksSummary
    fetchUser();
    // eslint-disable-next-line
  }, [id]);

  const fetchClient = async () => {
    try {
      const res = await axios.get(`/clients/${id}`);
      setClient(res.data);
      if (Array.isArray(res.data?.notes)) {
        setNotes(
          res.data.notes
            .slice()
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        );
      } else {
        setNotes([]);
      }
    } catch {
      toast.error('Failed to fetch client');
    }
  };

  const fetchUser = async () => {
    try {
      const res = await axios.get('/auth/profile');
      setUser(res.data);
    } catch {
      toast.error('Failed to fetch user info');
    }
  };

  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`/invoices?clientId=${id}`);
      const { items, total } = extractItemsAndTotal(res.data);
      setInvoices(Array.isArray(items) ? items : []);
      setInvoiceCount(Number(total) || (Array.isArray(items) ? items.length : 0));
    } catch {
      toast.error('Failed to fetch invoices');
      setInvoices([]);
      setInvoiceCount(0);
    }
  };

  const fetchPayments = async () => {
    try {
      const res = await axios.get(`/payments?clientId=${id}`);
      const { items } = extractItemsAndTotal(res.data);
      setPayments(Array.isArray(items) ? items : []);
    } catch {
      toast.error('Failed to fetch payments');
      setPayments([]);
    }
  };

  /** ✅ Unified Work fetch (badge + KPIs) */
  const fetchWorkSummary = async () => {
    try {
      // small page just for KPIs; badge uses meta/total
      const res = await axios.get('/works', { params: { clientId: id, limit: 200 } }); // ✅ FIX: /works
      const { items, total } = extractItemsAndTotal(res.data);
      setWorkCount(Number(total) || 0);
      setWorkItems(Array.isArray(items) ? items : []);
    } catch {
      toast.error('Failed to fetch work');
      setWorkCount(0);
      setWorkItems([]);
    }
  };

  /** ✅ Quotes (badge via meta/total) */
  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`/quotes`, { params: { clientId: id } });
      const { items, total } = extractItemsAndTotal(res.data);
      setQuotes(items);
      setQuoteCount(Number(total) || items.length);
    } catch {
      toast.error('Failed to fetch quotes');
      setQuotes([]);
      setQuoteCount(0);
    }
  };

  const handleDeleteInvoice = (iid) => handleDeleteItem('invoices', iid, fetchInvoices);

  const handleDeletePayment = (pid) =>
    handleDeleteItem('payments', pid, () => {
      fetchPayments();
      fetchInvoices();
    });

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff';
  const canEdit = isAdmin || isStaff;

  const counts = useMemo(
    () => ({
      quotes: typeof quoteCount === 'number' ? quoteCount : (Array.isArray(quotes) ? quotes.length : 0),
      invoices: typeof invoiceCount === 'number' ? invoiceCount : (Array.isArray(invoices) ? invoices.length : 0), // ✅ true total
      payments: Array.isArray(payments) ? payments.length : 0,
      work: typeof workCount === 'number' ? workCount : 0,
    }),
    [quoteCount, invoiceCount, quotes, invoices, payments, workCount]
  );

  /* -------------------- Notes -------------------- */
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const addNote = useCallback(async () => {
    const text = (noteDraft || '').trim();
    if (!text) return;
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      _id: tempId,
      note: text,
      createdAt: new Date().toISOString(),
      createdBy: user ? { _id: user.id || user._id, name: user.name, email: user.email } : undefined,
    };
    setNotes((prev) => [optimistic, ...prev]);
    setNoteDraft('');
    setSavingNote(true);

    try {
      const res = await axios.post(`/clients/${id}/notes`, { text });
      const updatedClient = res.data;
      setClient(updatedClient);
      const serverNotes = Array.isArray(updatedClient?.notes)
        ? updatedClient.notes.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        : [];
      setNotes(serverNotes);
      toast.success('Note saved');
    } catch (err) {
      setNotes((prev) => prev.filter((n) => n._id !== tempId));
      setNoteDraft(text);
      toast.error(err?.response?.data?.message || 'Failed to save note');
    } finally {
      setSavingNote(false);
    }
  }, [id, noteDraft, user]);

  const onChangeNote = useCallback((e) => setNoteDraft(e.target.value), []);

  /* -------------------- KPIs for Summary Strip (we keep currency guessing) ----- */
  // ✅ MINIMAL FIX: remove the USD fallback + stop overriding ClientSummaryStrip currency.
  // The strip should use company prefs / its own fallback logic.

  // (We still compute enriched invoices etc. for internal use if needed later)
  const paymentsByInvoiceId = useMemo(() => {
    const map = {};
    (payments || []).forEach((p) => {
      const key = p.invoiceId || p.invoice?._id || p.invoice;
      if (!key) return;
      map[key] = (map[key] || 0) + Number(p.amount || 0);
    });
    return map;
  }, [payments]);

  const enrichedInvoices = useMemo(() => {
    return (invoices || []).map((inv) => {
      const paid =
        typeof inv.totalPaid === 'number'
          ? inv.totalPaid
          : (paymentsByInvoiceId[inv._id] || 0);
      const total = Number(inv.totalAmount || 0);
      const remaining = Math.max(0, total - paid);
      return { ...inv, paid, remaining };
    });
  }, [invoices, paymentsByInvoiceId]);

  /* -------------------- Early skeleton -------------------- */

  if (!client || !user) {
    return (
      <div className="max-w-screen-2xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <Card className="p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-48 bg-gray-200 rounded" />
            <div className="h-4 w-96 bg-gray-200 rounded" />
            <div className="h-4 w-80 bg-gray-200 rounded" />
          </div>
        </Card>
      </div>
    );
  }

  /* -------------------- Tab renderer -------------------------------------- */

  const renderTab = () => {
    switch (activeTab) {
      case 'info':
        return (
          <ClientInfoTab
            client={client}
            canEdit={canEdit}
            onEdit={() => setShowEditModal(true)}
            notes={notes}
            noteDraft={noteDraft}
            onChangeNote={onChangeNote}
            onAddNote={addNote}
            savingNote={savingNote}
          />
        );
      case 'quotes':
        return (
          <ClientQuotesTab
            clientId={id}
            quotes={quotes}
            canEdit={canEdit}
            fetchQuotes={fetchQuotes}
          />
        );
      case 'invoices':
        return (
          <ClientInvoicesTab
            invoices={invoices}
            canEdit={canEdit}
            setEditingInvoice={setEditingInvoice}
            onDeleteInvoice={handleDeleteInvoice}
            fetchInvoices={fetchInvoices}
          />
        );
      case 'work':
        return (
          <ClientWorkTab
            clientId={id}
            canEdit={canEdit}
            onRefreshSummary={fetchWorkSummary} // ✅ refresh KPIs + badge after actions
          />
        );
      case 'payments':
        return (
          <ClientPaymentsTab
            payments={payments}
            canEdit={canEdit}
            onAddPayment={() => setShowPaymentModal(true)}
            onEditPayment={setEditingPayment}
            onDeletePayment={handleDeletePayment}
          />
        );
      default:
        return null;
    }
  };

  /* -------------------- New menu handlers (passed to TabsHeader) ---------- */
  const onNewQuote = () => {
    setOpenNewMenu(false);
    navigate(`/quotes/new?clientId=${id}`, {
      state: { clientId: id, clientName: client?.name || '' },
    });
  };
  const onNewInvoice = () => {
    setOpenNewMenu(false);
    navigate(`/invoices/new?clientId=${id}`, {
      state: { clientId: id, clientName: client?.name || '' },
    });
  };
  const onNewWork = () => {
    setOpenNewMenu(false);
    setShowWorkModal(true);
  };

  /* -------------------- Render ------------------------------------------- */

  return (
    <div className="min-h-[80vh] bg-gray-50">
      <div className="max-w-screen-2xl mx-auto pt-6 pb-10 px-4 sm:px-6 lg:px-8">
        {/* HERO */}
        <Hero client={client} canEdit={canEdit} onEdit={() => setShowEditModal(true)} />

        {/* SUMMARY STRIP — let it fetch its own KPIs + use its own currency resolution */}
        <div className="mt-6">
          <ClientSummaryStrip
            clientId={id}
            upcomingWindowDays={null}
            onNavigate={(tab) => setActiveTab(tab)}
          />
        </div>

        {/* CONTENT */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-9 space-y-6">
            <Card className="p-5">
              <TabsHeader
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                counts={counts}
                canEdit={canEdit}
                setOpenNewMenu={setOpenNewMenu}
                openNewMenu={openNewMenu}
                onNewQuote={onNewQuote}
                onNewInvoice={onNewInvoice}
                onNewWork={onNewWork}
              />

              <div className="mt-5">{renderTab()}</div>
            </Card>
          </div>

          {/* RIGHT RAIL */}
          <div className="lg:col-span-3">
            <RightRail client={client} />
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditClientModal client={client} onClose={() => setShowEditModal(false)} onSuccess={fetchClient} />
      )}

      {showWorkModal && (
        <AddWorkModal
          preselectedClientId={id}
          open={showWorkModal}
          onClose={() => setShowWorkModal(false)}
          onCreated={() => {
            fetchWorkSummary(); // ✅ refresh unified Work data
          }}
        />
      )}

      {showPaymentModal && (
        <AddPaymentModal
          clientId={id}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            fetchPayments();
            fetchInvoices();
          }}
        />
      )}
      {editingInvoice && (
        <EditInvoiceModal invoice={editingInvoice} onClose={() => setEditingInvoice(null)} onSuccess={fetchInvoices} />
      )}
      {editingPayment && (
        <EditPaymentModal payment={editingPayment} onClose={() => setEditingPayment(null)} onSuccess={fetchPayments} />
      )}
    </div>
  );
};

export default ClientDetail;
